// Court Vision shared primitives — the padel-line design language.
// SectionDeck, IconTile, ConfirmBar and EmptyStateRich are the building
// blocks every management screen composes.

import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

export function SectionDeck({ title, icon: Icon, meta, action, children }: {
  title: string
  icon?: LucideIcon
  meta?: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="deck">
      <div className="deck-header">
        <span className="deck-title">
          {Icon && <Icon size={14} />}
          {title}
        </span>
        {meta && <span className="deck-meta">{meta}</span>}
        {action && <span className="deck-action">{action}</span>}
      </div>
      {children}
    </section>
  )
}

export function IconTile({ icon: Icon, tone, size = 'md' }: {
  icon: LucideIcon
  tone?: 'accent' | 'success' | 'cta' | 'warning' | 'muted'
  size?: 'md' | 'lg'
}) {
  const toneClass = tone && tone !== 'accent' ? ` tone-${tone}` : ''
  return (
    <span className={`icon-tile${toneClass}${size === 'lg' ? ' lg' : ''}`}>
      <Icon size={size === 'lg' ? 22 : 18} />
    </span>
  )
}

export function ConfirmBar({ icon: Icon, message, confirmLabel, onConfirm, onCancel, disabled }: {
  icon: LucideIcon
  message: ReactNode
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
  disabled?: boolean
}) {
  return (
    <div className="confirm-bar">
      <IconTile icon={Icon} tone="cta" />
      <div className="confirm-bar-text">{message}</div>
      <div className="confirm-bar-actions">
        <button className="btn btn-cta" onClick={onConfirm} disabled={disabled}>{confirmLabel}</button>
        <button className="btn btn-secondary" onClick={onCancel}>Cancelar</button>
      </div>
    </div>
  )
}

export function EmptyStateRich({ icon: Icon, title, sub, action }: {
  icon: LucideIcon
  title: string
  sub?: string
  action?: ReactNode
}) {
  return (
    <div className="empty-state-rich">
      <IconTile icon={Icon} tone="muted" size="lg" />
      <div className="empty-state-rich-title">{title}</div>
      {sub && <div className="empty-state-rich-sub">{sub}</div>}
      {action}
    </div>
  )
}

export function StatStrip({ label, value, suffix, children }: {
  label: string
  value: ReactNode
  suffix?: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="stat-strip">
      <div>
        <div className="stat-strip-label">{label}</div>
        <div className="stat-strip-value">{value}</div>
      </div>
      {suffix}
      {children && <div style={{ marginLeft: 'auto', position: 'relative' }}>{children}</div>}
    </div>
  )
}
